import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { Button } from "@progress/kendo-react-buttons";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { Input, Switch } from "@progress/kendo-react-inputs";
import { icon } from "../../../../config";
import { addEmailAiPrompt, getEmailAiPrompt, updateEmailAiPrompt } from "../../../../shared/services/workflow.services";
import DescriptionPopup from "../../WorkflowBasicElements/DescriptionPopup";
import { useWorkflowContext } from "../../WorkflowContextProvider";
import { workflowQuote } from "../../workflow.constants";
import "./RuleForm.scss";
import { DELETE_ACTION_ID, labels } from "./ruleEngine.constants";

/**
 * Form header component with title and controls
 * @author Sarthak Arora
 */
export const FormHeader = ({ formData, setFormData, editingRule, handleDelete }) => (
  <div className='rule-form-header'>
    <div>
      <h6 className='m-0'>{editingRule ? labels.FORM.TITLE_EDIT : labels.FORM.TITLE_CREATE}</h6>
    </div>
    <div className='header-actions'>
      {editingRule && (
        <div className='d-flex align-items-center'>
          <Switch checked={!formData.isDisabled} onChange={() => setFormData((prev) => ({ ...prev, isDisabled: !prev.isDisabled }))} />
          <span className='mr-3'>{formData.isDisabled ? labels.FORM.STATUS_DISABLED : labels.FORM.STATUS_ACTIVE}</span>
        </div>
      )}
      {editingRule && (
        <span id='delete-rule-btn' onClick={() => handleDelete(editingRule.id)} icon='trash' look='flat' className='delete-button cursor-pointer'>
          {icon.DELETE_ICON}
        </span>
      )}
    </div>
  </div>
);

/**
 * Name field component
 * @author Sarthak Arora
 */
export const NameField = ({ formData, nameChangeHandler, errors }) => (
  <div className='form-group'>
    <div className='d-flex justify-content-between'>
      <label htmlFor='ruleName'>{labels.FORM.NAME_LABEL}</label>
      {errors.name && <div className='rule-form-error-text'>{errors.name}</div>}
    </div>

    <Input id='ruleName' autoFocus placeholder={labels.FORM.NAME_PLACEHOLDER} value={formData.name} onChange={nameChangeHandler} />
  </div>
);

/**
 * Locations section component
 * @author Sarthak Arora
 */
export const LocationsSection = ({ formData, handleLocationChange, errors }) => (
  <div className={`form-group ${errors.locations ? "has-error" : ""}`}>
    <div className='d-flex justify-content-between align-items-center'>
      <label className='m-0'>{labels.FORM.LOCATION_LABEL}</label>
      {errors.locations && <div className='rule-form-error-text'>{errors.locations}</div>}
    </div>
    <div className='checkbox-group'>
      <label>
        <input type='checkbox' checked={formData.locations.sender} onChange={() => handleLocationChange("sender")} />
        {labels.FORM.LOCATION_SENDER}
      </label>
      <label>
        <input type='checkbox' checked={formData.locations.subject} onChange={() => handleLocationChange("subject")} />
        {labels.FORM.LOCATION_SUBJECT}
      </label>
      <label>
        <input type='checkbox' checked={formData.locations.body} onChange={() => handleLocationChange("body")} />
        {labels.FORM.LOCATION_BODY}
      </label>
    </div>
  </div>
);

/**
 * Condition item component for each condition type
 * @author Sarthak Arora
 */
export const ConditionItem = ({ condition, index, conditionLabel, handleConditionToggle, handleConditionChange }) => (
  <div className={`condition ${condition.isDisabled ? "disabled" : ""}`}>
    <div className='d-flex justify-content-between'>
      <div>
        <label>
          <input type='checkbox' checked={!condition.isDisabled} onChange={() => handleConditionToggle(index)} />
          {conditionLabel}
        </label>
      </div>
      <div className='logic-switch d-flex align-items-center'>
        <label className='mb-0 mr-2'>{labels.FORM.LOGIC_LABEL}</label>
        <Switch checked={condition.logic === "and"} onChange={() => handleConditionChange(index, "logic", condition.logic === "and" ? "or" : "and")} disabled={condition.isDisabled} />
        <span className='logic-operator'>{condition.logic === "and" ? labels.FORM.LOGIC_AND : labels.FORM.LOGIC_OR}</span>
      </div>
    </div>
    <div className='condition-row'>
      <MultiSelect
        data={condition.keywords}
        value={condition.keywords}
        onChange={(e) => handleConditionChange(index, "keywords", e.target.value)}
        allowCustom
        placeholder={labels.FORM.KEYWORDS_PLACEHOLDER}
        disabled={condition.isDisabled}
        popupSettings={{ className: "hidden-dropdown" }}
      />
    </div>
  </div>
);

/**
 * Conditions section component
 * @author Sarthak Arora
 */
export const ConditionsSection = ({ formData, handleConditionToggle, handleConditionChange, errors }) => (
  <div className={`form-group ${errors.conditions ? "has-error" : ""}`}>
    <div className='d-flex justify-content-between align-items-center'>
      <label>{labels.FORM.CONDITIONS_LABEL}</label>
      {errors.conditions && <div className='rule-form-error-text'>{errors.conditions}</div>}
    </div>

    <div className='conditions-group'>
      <ConditionItem
        condition={formData.conditions[0]}
        index={0}
        conditionLabel={labels.FORM.CONDITION_CONTAINS}
        handleConditionToggle={handleConditionToggle}
        handleConditionChange={handleConditionChange}
      />
      <ConditionItem
        condition={formData.conditions[1]}
        index={1}
        conditionLabel={labels.FORM.CONDITION_NOT_CONTAINS}
        handleConditionToggle={handleConditionToggle}
        handleConditionChange={handleConditionChange}
      />
    </div>
  </div>
);

/**
 * Actions section component
 * @author Sarthak Arora
 */
export const ActionsSection = (props) => {
  const { formData, actions, isActionSelected, handleActionChange, errors, isDefaultRule, editingRule, defaultRuleId } = props;
  const { setAiSummaryPrompt, savedAiSummaryPrompt, workflow, aiSummaryPrompt, setSavedAiSummaryPrompt, setShowDescPopup } = useWorkflowContext();

  const { workflowId } = useParams();
  const user = useSelector((state) => state.auth.user);
  const [emailRuleDescPopup, setEmailRuleDescPopup] = useState(false);

  const showCustomizeAiInsightPrompt = useMemo(() => (editingRule || isDefaultRule) && isActionSelected(7), [editingRule, isDefaultRule, isActionSelected]);

  /**
   * Handles save/update ai email summary.
   * @author Sarthak Arora
   */
  const handleSaveAiSummaryPrompt = async () => {
    const cleanedPrompt = aiSummaryPrompt?.trim();
    const contentWithoutEmptyTags = cleanedPrompt?.replace(/<p>(\s|&nbsp;|<br>)*<\/p>/g, "")?.trim();
    const isEmptyHtml = !contentWithoutEmptyTags;

    const payload = {
      text: isEmptyHtml ? null : aiSummaryPrompt,
      companyId: user?.companyId,
      workflowId: workflowId,
      createdBy: user?.id,
    };
    savedAiSummaryPrompt ? await updateEmailAiPrompt({ promptId: savedAiSummaryPrompt?.promptId, text: isEmptyHtml ? null : aiSummaryPrompt }) : await addEmailAiPrompt(payload);
    setSavedAiSummaryPrompt({ ...savedAiSummaryPrompt, text: isEmptyHtml ? null : aiSummaryPrompt });
  };

  /**
   * Fetch and set the AI summary prompt for a given workflow if it is enabled.
   * @author Sarthak Arora
   */
  const fetchEmailAiPrompt = async () => {
    const emailAiprompt = await getEmailAiPrompt({ workflowId: workflowId, companyId: user?.companyId, type: "emailRule", ruleId: isDefaultRule ? defaultRuleId : editingRule?.id });
    setAiSummaryPrompt(emailAiprompt?.text ?? "");
    setSavedAiSummaryPrompt(emailAiprompt);
  };

  /**
   * Shows the summary description popup.
   * @author Sarthak Arora
   */
  const handleShowSummaryDescPopup = async () => {
    if (workflowId) {
      await fetchEmailAiPrompt();
    }
    setShowDescPopup(true);
    setEmailRuleDescPopup(true);
  };

  /**
   * Closes the summary description popup.
   * @author Sarthak Arora
   */
  const closeSummaryDescriptionPopup = () => {
    setShowDescPopup(false);
    setEmailRuleDescPopup(false);
  };

  return (
    <div className={`form-group ${errors.actions ? "has-error" : ""}`}>
      <div className={`d-flex align-items-center ${isDefaultRule ? "justify-content-start" : "justify-content-between"}`}>
        <label className='m-0 font-weight-normal'>{labels.FORM.ACTIONS_LABEL}</label>
        {isDefaultRule && <span className='default-rule-subtext'>{labels.DEFAULT_RULE_SUBTEXT}</span>}
        {errors.actions && !isDefaultRule && <div className='rule-form-error-text'>{errors.actions}</div>}
      </div>
      <div className='actions-group'>
        {actions.map((action) => {
          const isActionDisabled = (action?.Id === 5 || action?.Id === 7) && !isActionSelected(1);
          return (
            <React.Fragment key={action?.Id}>
              <label className='actions-group-label'>
                <input className='actions-group-checkbox' type='checkbox' disabled={isActionDisabled} checked={isActionSelected(action?.Id)} onChange={() => handleActionChange(action?.Id)} />
                <span className='form-action-icons'>{icon[action?.Icon]}</span>
                {action?.Action}
              </label>
              {showCustomizeAiInsightPrompt && action?.Id === 7 && (
                <>
                  <div className='d-flex align-items-center ml-2'>
                    <span id='form-section-edit-description-btn' className='form-action-icons cursor-pointer' onClick={handleShowSummaryDescPopup} title={labels.FORM.EDIT_DESCRIPTION}>
                      {icon.DESCRIPTION}
                    </span>
                    <label id='form-section-show-customize-ai-insight-prompt' className='mb-0 actions-group-label  cursor-pointer blue' onClick={handleShowSummaryDescPopup}>
                      {workflowQuote.CUSTOMIZE_AI_INSIGHT_PROMPT}
                    </label>
                  </div>
                  {emailRuleDescPopup && (
                    <DescriptionPopup
                      desc={aiSummaryPrompt}
                      setDesc={setAiSummaryPrompt}
                      showDescPopup={emailRuleDescPopup}
                      setShowDescPopup={setEmailRuleDescPopup}
                      workflow={workflow}
                      updateDescription={handleSaveAiSummaryPrompt}
                      closeDescriptionPopup={closeSummaryDescriptionPopup}
                      acknowledgeMailEditor={true}
                      headerLabel={workflowQuote.CUSTOMIZE_AI_INSIGHT_PROMPT}
                      taskKey={"emailRule"}
                    />
                  )}
                </>
              )}
            </React.Fragment>
          );
        })}
      </div>
      {isActionSelected(DELETE_ACTION_ID) && <div className='trash-note'>{labels.FORM.DELETE_WARNING}</div>}
    </div>
  );
};

/**
 * Form footer component with action buttons
 * @author Sarthak Arora
 */
export const FormFooter = ({ onClose, handleSubmit, errors, editingRule }) => (
  <div className='rule-form-footer'>
    <Button id='cancel-rule-btn' look='outline' onClick={onClose}>
      {labels.FORM.CANCEL_BUTTON}
    </Button>
    <Button id='save-rule-btn' className='btn btn-primary' onClick={handleSubmit} disabled={Object.keys(errors).length > 0}>
      {editingRule ? labels.FORM.SAVE_BUTTON : labels.FORM.ADD_BUTTON}
    </Button>
  </div>
);
